import { useEffect, useState } from 'react';
import { disablePush, enablePush, getPushState, type PushState } from '../push';
import { useT, type DictKey } from '../i18n';

// Блок управления push-уведомлениями в SettingsPage. Сам по себе — изолированный
// компонент, чтобы Test/SSR не падали в окружении без Notification/SW.

export function PushSettings() {
  const t = useT();
  const [state, setState] = useState<PushState | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getPushState()
      .then((s) => !cancelled && setState(s))
      .catch(() => !cancelled && setState({ status: 'idle' }));
    return () => {
      cancelled = true;
    };
  }, []);

  async function handle(action: 'enable' | 'disable') {
    setError(null);
    setWorking(true);
    try {
      const next = action === 'enable' ? await enablePush() : await disablePush();
      setState(next);
    } catch {
      setError(t('settings.push.failed'));
    } finally {
      setWorking(false);
    }
  }

  if (!state) {
    return (
      <Wrapper title={t('settings.push.title')}>
        <p className="text-sm text-slate-500">{t('common.loading')}</p>
      </Wrapper>
    );
  }

  const statusText = t(statusMessageKey(state.status));
  const canDisable = state.status === 'subscribed';
  const canEnable = state.status === 'idle';

  return (
    <Wrapper title={t('settings.push.title')}>
      <p className="text-sm text-slate-900 mb-1">{statusText}</p>
      {canDisable && (
        <p className="text-xs text-slate-500 mb-3">{t('settings.push.subscribedHint')}</p>
      )}
      {error && <p role="alert" className="text-xs text-red-600 mb-2">{error}</p>}

      {canDisable && (
        <button
          type="button"
          disabled={working}
          onClick={() => handle('disable')}
          className="rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
        >
          {working ? t('settings.push.working') : t('settings.push.disable')}
        </button>
      )}
      {canEnable && (
        <button
          type="button"
          disabled={working}
          onClick={() => handle('enable')}
          className="rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {working ? t('settings.push.working') : t('settings.push.enable')}
        </button>
      )}
    </Wrapper>
  );
}

function Wrapper({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4 rounded-md border border-slate-200 bg-white p-4 shadow-card">
      <label className="mb-2 block text-xs text-slate-500">{title}</label>
      {children}
    </div>
  );
}

function statusMessageKey(s: PushState['status']): DictKey {
  switch (s) {
    case 'unsupported':
      return 'settings.push.unsupported';
    case 'server_disabled':
      return 'settings.push.serverDisabled';
    case 'permission_denied':
      return 'settings.push.permissionDenied';
    case 'subscribed':
      return 'settings.push.subscribed';
    case 'idle':
    default:
      return 'settings.push.idle';
  }
}
